/**
 * QuoteReviewSend — Full-page output hub for reviewing and sending a quote.
 *
 * Phase A8 spec:
 * - Full-page route: /quotes/:id/review (NOT inside the builder, NOT a modal)
 * - Live preview (left) + controls (right)
 * - Recipients multi-select (customer contacts) + inline add new
 * - Display toggles (line-item pricing, labor pricing, notes, attachments)
 * - T&C template chosen + previewed here
 * - Footer: Cancel · Download PDF · Send Quote (primary) → confirmation → Quotes landing
 *
 * THIS IS THE ARCHETYPE for Iter 10 invoice-send (mirror this pattern).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Checkbox } from '../ui/Checkbox';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';

import { useToast } from '../ui/Toast';
import { SelectMenu } from '../ui';
import { quotesAPI, resolveDisplayStatus, useQuoteAutoSave } from '../../quote-builder';
import { getQuoteLockState, quoteLockLabel } from '../../quote-builder/quoteLifecycle';
import buildViewQuote from '../../quote-builder/buildViewQuote';
import { isOptionLine } from '../../quote-builder/separateOptions';
import { getQuoteRoute } from '../../utils/quoteRouting';
import { quoteAPI } from '../../services/api';
import { useSettingsContext } from '../../hooks/useSettings';
import useCompanyProfile from '../../hooks/useCompanyProfile';
import useBranding from '../../hooks/useBranding';
import QuoteDocument from './QuoteDocument';
import ReviewAndSendShell, { seedRecipientsFromContacts } from '../shared/ReviewAndSendShell';
import SigningLinkResult, { resolveSigningLink } from '../shared/SigningLinkResult';
import SendComposerCc, { withComposerCc } from '../shared/SendComposerCc';
import { getTcTemplateId } from '../../utils/canonicalFields';
import { loadTCTemplates } from '../../utils/loadTCTemplates';
import { AttachModal } from '../attachments/AttachModal';
import { canIncludeInPdf, pdfRefusalMessage, storedChoice } from '../../utils/quotePdfFiles';
import { PaperClipIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';

// PR-B1 (2026-07-21): formatEnum + prettifyEnum retired. Enum → label resolution
// now goes through `useSettingsContext().getEnumLabel(listType, value, { quoteDefaultsField })`,
// which implements the canonical 4-step precedence chain (see hooks/useSettings.js).

// PR-4 §1b · sumDiscounts ---------------------------------------------------
// Builder shape: `{type: 'percent'|'fixed', value, description}`.
// Returns { total: <cents>, rows: [{id, label, amount: <cents>}] }.
// `subtotalCents` is the base for percent discounts. `fixed` values are
// dollars in the DiscountSection UI — convert to cents.
// PT-30 (2026-07-26): pure helpers for building the margin context. They live
// in this file (not in marginEngine) because they concern shape-normalization
// on the customer/settings objects rather than the engine itself. See
// docs/pt-30/PRECEDENCE.md.

// Normalize a `default_margin` value coming off a customer record. The form
// accepts 0–100 (percentage); some legacy records may already be 0..1 decimal.
// Returns null for unset/invalid so the engine cascades to the next level.
// W3.2 (2026-08-04): normalizeMarginPct + resolveCustomerTypeMargin removed
// (only consumer was the Internal Summary card, now deleted).

// Look up the Lindsey level-3 margin for a customer by resolving their
// customer_type in the tenant's settings-managed list of customer types.
// `getSettingsOptions('customer_types')` returns [{value, label, ...}] where
// entries may include `default_margin_pct` (added by CustomerTypesPage per PT-30).

// ─── Display Toggle Options ──────────────────────────────────────────────────

// PR-02c toggle enumeration — authoritative list (from deployed code, not wire QR-1).
// Every toggle here is consumed by <QuoteDocument>. Adding a toggle without a
// consumer is a silent capability regression; removing one without an operator
// ruling is a customer-facing capability removed by omission (see learning
// "display toggles are load-bearing UX contracts").
//
const DISPLAY_TOGGLES = [
  { id: 'groupByArea', label: 'Group by area', defaultOn: true },
  { id: 'showSqft', label: 'Show square footage', defaultOn: true },
  { id: 'lineItemPricing', label: 'Show line-item pricing', defaultOn: true },
  {
    id: 'laborPricing',
    label: 'Show labor pricing breakdown',
    defaultOn: false,
  },
  { id: 'showTax', label: 'Show tax', defaultOn: true },
  { id: 'notes', label: 'Include notes', defaultOn: true },
  // PT-15 #8d (2026-07-24): the `headlineNotes` toggle was removed.
  // It let the operator print quote-level notes as a block at the top of
  // the document AND (via the regular `notes` toggle) at the bottom — the
  // same content printed twice. Users found it confusing; the bottom-of-doc
  // placement (governed by `notes`) is the wire-canonical location.
  //
  // §1 (2026-08-08, RS-1 fix): `attachments: 'Include attachments'` toggle
  // removed — it asked the user to decide about content they couldn't see.
  // Attachments now render as a dedicated sidebar card with a per-file list
  // + include checkboxes (see extraRailCards below).
];

// IDI-568 (D3): Review & Send starts from the latest send's stored values
// (quote.displayOptions, quote.sendComposer), else today's defaults — also
// after a Reopen — so the locked Download and "Resend quote" reproduce what
// was sent.
function restoreDisplayOptions(quote) {
  const defaults = Object.fromEntries(DISPLAY_TOGGLES.map((t) => [t.id, t.defaultOn]));
  const stored = quote?.displayOptions;
  if (!stored || typeof stored !== 'object') return defaults;
  const out = { ...defaults };
  DISPLAY_TOGGLES.forEach((t) => {
    if (typeof stored[t.id] === 'boolean') out[t.id] = stored[t.id];
  });
  return out;
}

function restoreRecipients(seeded, sendComposer) {
  const sent = Array.isArray(sendComposer?.recipients) ? sendComposer.recipients : [];
  if (!sent.length || !Array.isArray(seeded) || !seeded.length) return seeded;
  const ids = new Set(sent.map((r) => r?.contact_id ?? r?.contactId).filter(Boolean).map(String));
  const emails = new Set(sent.map((r) => String(r?.email || '').trim().toLowerCase()).filter(Boolean));
  const matches = (r) => (r.contactId && ids.has(String(r.contactId)))
    || (r.id && ids.has(String(r.id)))
    || emails.has(String(r.email || '').trim().toLowerCase());
  if (!seeded.some(matches)) return seeded; // keep the primary-contact default
  return seeded.map((r) => ({ ...r, checked: !!matches(r) }));
}

const attachmentKey = (a) => a?.attachmentId || a?.id || a?.attachment_id || '';

// IDI-487 (D6): a never-sent quote's files open unticked — the dealer picks
// what reaches the customer (email) and the downloaded PDF. IDI-758 (D1): so
// does a quote whose stored list predates the cut-off (storedChoice).
function restoreAttachmentInclusion(files, sendComposer) {
  const sent = storedChoice(sendComposer);
  if (!Array.isArray(sent)) return files.map((a) => ({ ...a, included: false }));
  const keys = new Set(sent.map(attachmentKey).filter(Boolean).map(String));
  return files.map((a) => ({ ...a, included: keys.has(String(attachmentKey(a))) }));
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * computeReconciliationWarning — pure helper extracted from the R&S
 * reconciliation guard (DEF-115) so the option-filter fix (Q-15) is
 * testable in isolation.
 *
 * @param {object|null} quote
 * @returns {string|null}  the banner copy, or null when no warning fires
 */
export function computeReconciliationWarning(quote) {
  if (!quote) return null;
  const storedSubtotal = quote.subtotal ?? quote.subtotal_cents ?? null;
  if (storedSubtotal === null || storedSubtotal === 0) return null;
  const lines = quote.line_items || quote.lineItems || [];
  if (lines.length === 0) return null;
  // Q-15 (2026-08-18): options are opt-in per §3k and NEVER counted in the
  // quote total. Before this fix, the banner on quote 2216 fired with lineSum
  // $10,116.44 vs stored $7,602.69 — the mismatch was the operator's own
  // OPTION lines ($2,513.75) that the document correctly excluded.
  const baseLines = lines.filter((li) => !isOptionLine(li));
  const lineSum = baseLines.reduce((s, li) => {
    if (!li) return s;
    const v = Number(li.line_total ?? li.lineTotal);
    return s + (Number.isFinite(v) ? v : 0);
  }, 0);
  if (lineSum === 0) return null;
  if (Math.abs(storedSubtotal - lineSum) <= 1) return null;
  return `This quote's stored subtotal ($${(storedSubtotal / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}) doesn't match its line items ($${(lineSum / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}). The document below shows the correct line-item total.`;
}

export default function QuoteReviewSend() {
  const { id } = useParams();
  const navigate = useNavigate();
  // The Toast API is `{ show, success, error, info, warning }` — destructure to the canonical
  // shape. (Prior code destructured `showToast` which is not a member of the returned object;
  // every toast call was a silent no-op.)
  const toast = useToast();
  // IDI-133 (2026-09-01): useConfirm() was only used by the removed handleMarkSent.
  // No consumer left; removed to satisfy no-unused-vars.

  const [quote, setQuote] = useState(null);
  // PR-02d: live customer for lifecycle-keyed field resolution. See
  // context/learnings/2026-07-20-quoting-pr02-banked-learnings.md #4.
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // IDI-308: the send result (signing link + delivery outcome). Set => show it.
  const [sendResult, setSendResult] = useState(null);

  // Recipients
  const [recipients, setRecipients] = useState([]);
  // PT-34: new-recipient input state now lives in the shell.
  // const [newRecipientEmail, setNewRecipientEmail] = useState('');
  // PT-14 B2 (Wave 2, 2026-07-24): reveal-CTA state for the manual email
  // input in the recipients rail. Hidden by default; toggled by CTA.
  // PT-34: recipients add-new is now handled by the shell.
  // const [showAddRecipient, setShowAddRecipient] = useState(false);

  // PR-20 (2026-07-22): composer fields (subject + message + e-sign toggle).
  // Modal-hosted above a dimmed R1. Fields are local for now; backend send
  // channel is outside BIDIT at launch (send channel ruling) so these carry
  // the customer-facing shape without requiring an API contract yet.
  const [composerSubject, setComposerSubject] = useState('');
  const [composerMessage, setComposerMessage] = useState('');
  // W3.6 (R20-28, 2026-08-04): e-signature is always requested on send.
  // The checkbox was removed per operator ruling — e-sig is not optional.
  const requireESign = true;
  const [includeQuotePdf, setIncludeQuotePdf] = useState(true);
  // IDI-134: CC chip row. null = not seeded yet; SendComposerCc seeds it once
  // with the signed-in user + assigned salesperson(s). Removing every chip is
  // the "off" state and omits `cc` from the payload entirely.
  const [ccEmails, setCcEmails] = useState(null);

  // Display toggles
  const [displayOptions, setDisplayOptions] = useState(
    Object.fromEntries(DISPLAY_TOGGLES.map((t) => [t.id, t.defaultOn]))
  );

  // T&C template
  const [tcTemplateId, setTcTemplateId] = useState('');

  // PR-02c: T&C templates loaded from settings so the selected template's body
  // can be rendered on the customer document (plan §2).
  const [tcTemplates, setTcTemplates] = useState([]);

  // PR-36 §1.4 (2026-08-17): tenant branding via shared hook. Was 20 lines
  // (comment + useState + useEffect fetch + silent catch). Now one line.
  const { branding } = useBranding();

  // PR-PT-02 item 3: rail notes editor (edits the quote-level notes)
  const [railNotes, setRailNotes] = useState('');

  // IDI-254 + IDI-280 (2026-09-13): persist edits made ON THIS PAGE, not just
  // load them. Before this, railNotes/tcTemplateId were LOAD-only — seeded
  // from the quote at mount (see the load effect below), edited freely in
  // this component's own state, but never written back anywhere. Confirmed
  // live before fixing: typed a new value into the Notes textarea here,
  // waited past the expected debounce, GET the quote directly — still the
  // OLD value. The builder's own Notes card (a different component,
  // metadata.notes) saves fine via its own useQuoteAutoSave instance; this
  // page had no equivalent.
  //
  // Reuses useQuoteAutoSave — the SAME mechanism the builder already uses —
  // rather than a bespoke save call, per the ruling: one hook, two
  // consumers. `dirtyCheckKeys` scopes the dirty-check to exactly the two
  // fields this page can edit (not the builder's full line-item/metadata
  // surface, which this page doesn't own). `stripPriceFields: true` is
  // deliberately NOT set here — neither notes nor tcTemplateId are
  // price-bearing, so this save is safe even on a price-locked
  // (acknowledged) quote; the builder's own instance is what gates price
  // fields, not this one.
  const rsAutoSavePayload = useMemo(() => ({
    notes: railNotes,
    tcTemplateId: tcTemplateId || null,
  }), [railNotes, tcTemplateId]);

  // IDI-254/IDI-280 review (2026-09-13): the hook's return was being
  // discarded entirely — the exact defect flagged on QuoteBuilderPage.js
  // before IDI-313 fixed it there. `error` (a hard save failure — network,
  // 5xx) and `lockedError` (a document_locked 409) both need to be
  // destructured and rendered, or a real failure is silent and the user
  // keeps editing believing their notes/T&C choice saved. `lockedError`
  // matters here specifically: the comment above argues notes/tcTemplateId
  // aren't price-bearing so a lock shouldn't fire — if that reasoning is
  // ever wrong, this is how it would be caught, not silently swallowed.
  // IDI-132: a sent or signed quote's Review & Send is the locked document
  // (an Acknowledgments row opens it). Notes and T&C are locked server-side
  // on a sent quote too, so the page doesn't autosave them while locked.
  const rsLockState = getQuoteLockState(quote);
  const {
    error: rsAutoSaveError,
    lockedError: rsLockedError,
  } = useQuoteAutoSave(id, rsAutoSavePayload, {
    apiCreate: null, // this page only ever edits an EXISTING quote
    dirtyCheckKeys: ['notes', 'tcTemplateId'],
    // Gated on the load effect having completed (loading flips false at
    // :272/:364 below) — without this, the hook starts watching
    // railNotes/tcTemplateId from their pre-seed initial empty-string
    // values, and a debounce firing before the seed effect runs would
    // write blanks over real saved data. This is exactly the class of bug
    // the ruling called out: "a save path without a load path means the
    // first autosave tick overwrites saved notes with blank" — the fix
    // for that is not skipping the seed, it's not saving until AFTER it.
    enabled: !loading && !rsLockState,
  });
  // §1 (2026-08-08, RS-1/RS-3): attachments now render as a dedicated sidebar
  // card with a per-file list + include checkboxes. `attachedFiles` seeds from
  // `data.attachments` at load; each entry carries an `included` boolean the
  // user can toggle. `attachOpen` gates the AttachModal.
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [attachOpen, setAttachOpen] = useState(false);

  // Company profile (name, address, phone, email) used in the document header.
  const { profile: companyProfile } = useCompanyProfile();

  // PR-B1 (2026-07-21): centrally-managed settings + enum → label helper. Replaces
  // the prior imperative fetches for `customer_types`, `payment_terms`, `payment_types`
  // (three useEffects worth of code) and the local `CUSTOMER_TYPES_FALLBACK` map.
  // See hooks/useSettings.js docstring for the 4-step precedence chain.
  const { getOptions: getSettingsOptions, getEnumLabel } = useSettingsContext();

  // PR-26 §1 (302_): shared loader with default-terms fallback. Same helper
  // used by ChangeOrderReviewSend and the builder's Choose T&C picker.
  useEffect(() => {
    let cancelled = false;
    loadTCTemplates().then((items) => {
      if (cancelled) return;
      setTcTemplates(items);
      // PR-32 (308_ §3): auto-select the single default terms option so
      // quotes don't go out bare. Only fires when nothing is already selected
      // (dealer's explicit choice always wins) AND the only option is the
      // synthetic 'default'. Real templates authored by the tenant are NOT
      // auto-selected — the dealer picks intentionally.
      if (items.length === 1 && items[0].id === 'default') {
        setTcTemplateId((current) => current || 'default');
      }
    });
    return () => { cancelled = true; };
  }, []);


  // ─── Load Quote ────────────────────────────────────────────────────────────

  useEffect(() => {
    async function loadQuote() {
      let data;
      // IDI-37: the quote fetch is the ONLY step that should abort the load.
      // Everything after it is independent seeding, and each piece is now
      // guarded separately.
      //
      // Previously the whole body sat in one try/catch whose handler just fired
      // `toast.error('Failed to load quote')`. A throw in ANY seeding step — T&C
      // resolution, attachments, notes — silently skipped every later step,
      // including the customer fetch that seeds the recipient list. The live
      // symptom was "No recipients. Add at least one email address." on a quote
      // whose customer has a perfectly good contact, with no usable error.
      //
      // A failure in optional seeding must degrade that one field, not wipe the
      // recipient list and leave the user unable to email their customer.
      try {
        data = await quotesAPI.getQuote(id);
        setQuote(data);
        // IDI-568 (D3): the latest send's toggles and PDF choice, else defaults.
        setDisplayOptions(restoreDisplayOptions(data));
        if (typeof data?.sendComposer?.include_quote_pdf === 'boolean') {
          setIncludeQuotePdf(data.sendComposer.include_quote_pdf);
        }
      } catch (err) {
        toast.error('Failed to load quote');
        setLoading(false);
        return;
      }

      try {
        // PR-02d: fetch the current customer alongside the quote so we can
        // live-resolve customer-scoped display fields for pre-send quotes.
        // Backend does NOT join customers into quote GET; we do it here.
        //
        // IDI-37: this is also the SOURCE OF TRUTH for recipients, so a failure
        // here is user-visible and must be reported — not swallowed.
        const cid = data?.customerId ?? data?.customer_id;
        let custRecord = null;
        if (cid) {
          try {
            custRecord = await quotesAPI.getCustomer(cid);
            setCustomer(custRecord || null);
          } catch (err) {
            setCustomer(null);
            toast.error('Could not load the customer — add recipients manually.');
          }
        } else {
          // No customer id on the quote at all. Recipients cannot be seeded, and
          // the user needs to know why rather than seeing an empty list.
          toast.error('This quote has no customer on file — add recipients manually.');
        }

        // §4 (2026-07-29): recipient seeding — read/write trace.
        //
        // WRITE (`assembleQuotePayload`): sends `contact_id` (singular), NOT `contacts[]`.
        // The quote record therefore never carries a contacts array.
        // READ (previous code): looked at `data.contacts` — always empty. Then fell
        // back to `data.customer_email` — also never persisted. Result: zero recipients
        // on a customer with contacts (the exact live symptom).
        //
        // FIX: source of truth for contacts is the CUSTOMER record (fetched above).
        // Use the shared `seedRecipientsFromContacts` per PT-46 46.4c so Quote / CO /
        // Invoice R&S surfaces stay consistent.
        const customerContacts = Array.isArray(custRecord?.contacts) ? custRecord.contacts : [];
        const seeded = seedRecipientsFromContacts(
          // Prefer customer contacts (source of truth); accept quote-stamped contacts
          // if a future write path adds them; fall back to empty.
          customerContacts.length > 0 ? customerContacts : (Array.isArray(data?.contacts) ? data.contacts : []),
          { email: custRecord?.email || data?.customer_email || '', name: custRecord?.name || data?.customer_name || '' }
        );
        // IDI-568 (D3): re-tick the latest send's recipients.
        setRecipients(restoreRecipients(seeded, data?.sendComposer));
      } catch (err) {
        // Recipient seeding is load-bearing: without it the user cannot send.
        toast.error('Could not load recipients — add them manually before sending.');
      }

      // ── Optional seeding below. Each step degrades on its own. ──

      try {
        // PR-20 (2026-07-22): seed composer subject with a sensible default.
        const quoteNum = data?.quote_number || data?.quoteNumber || '';
        setComposerSubject(quoteNum ? `Quote ${quoteNum}` : 'Your quote');
      } catch { /* subject falls back to the placeholder */ }

      try {
        // PR-PT-02: seed rail notes from quote
        setRailNotes(data?.notes || data?.metadata?.notes || '');
      } catch { /* notes stay empty */ }

      try {
        // §1 (2026-08-08): seed attached files from quote (IDI-487: a
        // never-sent quote's open unticked). Users tick per file. The
        // `included` flag is local to R&S — the canonical attachment record on the quote is unchanged; we just choose
        // which to include when the customer receives the document.
        // IDI-123 (2026-09-09): read BOTH `documents[]` (canonical) AND
        // `attachments[]` (legacy) and merge, same as SentQuoteRightPanel.
        const rawDocs = Array.isArray(data?.documents) ? data.documents : [];
        const rawLegacy = Array.isArray(data?.attachments) ? data.attachments : [];
        const seen = new Set();
        const merged = [...rawDocs, ...rawLegacy].filter((a) => {
          const k = a.attachmentId || a.id || JSON.stringify(a);
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        // IDI-568 (D3): the latest send's attachment choice; IDI-487 (D6):
        // else none ticked.
        setAttachedFiles(restoreAttachmentInclusion(merged, data?.sendComposer));
      } catch { /* attachment list stays empty */ }

      try {
        // N-14 conformant: canonical accessor with divergence detection.
        // Canonical key: `tcTemplateId` (top-level camel). PR-26 note (302_ §1):
        // the prior comment claimed assembleQuotePayload writes this — it does not.
        // The builder's ChooseTCTemplateModal sets metadata.tcTemplateId directly.
        const seededTcId = getTcTemplateId(data, id) || '';
        if (seededTcId) setTcTemplateId(seededTcId);
      } catch { /* user picks a T&C template manually */ }

      setLoading(false);
    }
    if (id) loadQuote();
  }, [id, toast]);

  // ─── Actions ───────────────────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    // PT-53A W2.1 (DEF-108): compose the send payload from the actual
    // composer state so recipients / subject / message / requireESign
    // are persisted on the quote record instead of being silently
    // discarded. `§silent success is worse than visible failure` —
    // the R&S modal literally displayed "recipients recorded" while
    // the API call was `markSent(id)` with NO body.
    //
    // IDI-118 (2026-09-03): requireESign has been hardcoded true since W3.6
    // (2026-08-04) — the e-sig checkbox was removed by operator ruling and
    // never came back, so the markSent/`?action=send` fallback below was
    // dead code (unreachable from any UI path). Removed along with the
    // backend `?action=send` alias (see quotes/main.go, quoteAPI.js).
    setSending(true);
    try {
      const baseComposer = {
        recipients: recipients.filter((r) => r.checked).map((r) => ({
          contact_id: r.contactId ?? r.contact_id ?? null,
          name: r.name ?? '',
          email: r.email ?? '',
        })),
        subject: composerSubject,
        message: composerMessage,
        include_quote_pdf: includeQuotePdf,
        require_esign: requireESign,
        composed_at: new Date().toISOString(),
        // §1 (2026-08-08, RS-3): send only the attachments the user opted in for.
        // Map back to attachment refs (drop the local `included` flag).
        included_attachments: attachedFiles
          .filter((a) => a.included !== false)
          .map((a) => {
            const { included, ...ref } = a;
            return ref;
          }),
      };
      // IDI-134: only send `cc` when at least one address remains; never an
      // empty array the backend has to interpret.
      const composer = withComposerCc(baseComposer, ccEmails);
      const res = await quotesAPI.sendForSignature(id, composer, displayOptions);
      // IDI-308: never navigate away from the link. Every delivery outcome
      // (sent, partial, no_recipient, not_configured, unknown) shows the
      // signing link with Copy -- it's the e-sign fallback (P-23). IDI-34:
      // the copy reports what ACTUALLY happened; only 'sent' claims an email.
      const token = res?.quote?.signatureToken || res?.signature_token || res?.signatureToken;
      const url = resolveSigningLink(res?.signature_link, token);
      if (url) {
        setSendResult({
          url,
          deliveryStatus: res?.delivery_status,
          deliveryDetail: res?.delivery_detail,
          sentTo: res?.sent_to,
          recipient: composer.recipients[0]?.email || '',
          expiresAt: res?.expires_at,
        });
        return;
      }
      // No link at all is a server fault, not a delivery outcome: say so and stay.
      toast.error('The quote was not given a signing link. Please try again.');
    } catch (err) {
      toast.error(`Failed to send: ${err.message || 'Unknown error'}`);
    } finally {
      setSending(false);
      setShowConfirm(false);
    }
  }, [id, recipients, ccEmails, composerSubject, composerMessage, includeQuotePdf, requireESign, attachedFiles, displayOptions, toast]);

  // PR-02c: Download = print the SAME document the user just reviewed.
  //
  // ARCHITECTURAL RULE (LAT-R14): the print rules in QuoteDocument.print.css are
  // UNCONDITIONAL. Cmd+P, File → Print, and this button all produce identical
  // output. This button exists only so users have an in-app affordance for the
  // browser print dialog; the isolation is structural, not handler-gated.
  //
  // Do NOT introduce a PDF library. Ever. (plan §1c.4)
  // PR-02c: Download = generate the server-side PDF (the same document the
  // customer receives) and trigger a file download. The Go generator
  // (internal/pdf/quote.go) is the sanctioned answer per PR #78.
  // Cmd+P still works — the print CSS is unconditional by design (LAT-R14).
  const handleDownloadPDF = useCallback(async () => {
    try {
      // IDI-359: send the toggles as previewed (e.g. "Include notes") so the
      // downloaded PDF's option notes match the preview.
      // IDI-487 (D5): the ticked PDFs and images follow the quote in one
      // file; with any, the backend answers a link to it.
      const ticked = attachedFiles
        .filter((a) => a.included !== false && canIncludeInPdf(a))
        .map(({ included, ...ref }) => ref);
      const res = await quoteAPI.generatePDF(id, displayOptions, ticked);
      const data = res?.data || res;
      if (data?.url) {
        const a = document.createElement('a');
        a.href = data.url;
        a.download = data?.filename || `${quote?.quoteNumber || quote?.quote_number || 'Quote'}.pdf`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        return;
      }
      if (!data?.pdf_base64) {
        toast.error('PDF generation failed \u2014 no data returned.');
        return;
      }
      const byteChars = atob(data.pdf_base64);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data?.filename || `${quote?.quoteNumber || quote?.quote_number || 'Quote'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // IDI-487: 'Couldn't add "<file>" to the PDF. Untick it and download again.'
      toast.error(pdfRefusalMessage(err) || err?.message || 'Failed to generate PDF. Please try again.');
    }
  }, [id, quote, displayOptions, attachedFiles, toast]);

  const handleCancel = useCallback(() => {
    navigate(getQuoteRoute(quote));
  }, [quote, navigate]);

  // IDI-133 completion (2026-09-01, Portica #40): Convert to Job in the R&S
  // header actions slot. Same navigation target as every other Convert site
  // (QuoteDetail, QuotesPage row menu, bulk bar): /quotes/:id/record-decision.
  const handleConvertToJob = useCallback(() => {
    navigate(`/quotes/${id}/record-decision`);
  }, [id, navigate]);

  // Predicate: convertible when status is active, sent, or won. Mirrors the
  // CONVERTIBLE_STATES set in QuotesPage.js. Includes 'draft' because the
  // R&S surface reads quote.status directly; backend-draft is UI-active per
  // quoteLifecycle.js normaliser.
  const canConvertToJob = ['active', 'sent', 'won', 'draft'].includes(
    (quote?.status || 'active').toLowerCase()
  );

  // IDI-133 (2026-09-01, completed in Portica #40 follow-up): handleMarkSent
  // and markSentHandler deleted. Send is now the only path to sent status.
  // handleConvertToJob added below for the R&S header actions slot.


  // PT-34: these handlers are now inlined in the shell's prop callbacks.
  // Kept as comments for audit trail; the shell's onToggleRecipient,
  // onAddRecipient, and onToggleDisplay props receive equivalent lambdas.

  // ─── Render ────────────────────────────────────────────────────────────────

  // PR-02c: customer-only viewQuote projection - the cost/margin firewall.
  //
  // Rule (plan section 4): the QuoteDocument component MUST NOT receive any cost
  // bucket (materialCost / laborCost / otherCost), margin rate, cost breakdown,
  // recipients, internal notes, or any other internal figure. Any value that
  // shouldn't reach a customer PDF is excluded HERE, so it cannot leak downstream
  // by accident. If a new field is added to the quote model and it belongs on the
  // document, add it here. If it doesn't belong on the customer document, DO NOT
  // add it here - there is no other data path into QuoteDocument.

  const selectedTerms = useMemo(() => {
    if (!tcTemplateId) return '';
    const match = tcTemplates.find((t) => (t.id || t.name) === tcTemplateId);
    return match ? (match.body || match.content || match.text || '') : '';
  }, [tcTemplateId, tcTemplates]);

  const viewQuote = useMemo(() => {
    return buildViewQuote(quote, customer, {
      getSettingsOptions,
      getEnumLabel,
      showNotes: displayOptions.notes !== false,
      notes: displayOptions.notes !== false ? (railNotes || '') : '',
      terms: selectedTerms,
    });
  }, [quote, customer, displayOptions, selectedTerms, railNotes, getEnumLabel, getSettingsOptions]);

  // PR-PT-02 item 7: internal margin summary (estimator-only, never on customer doc)
  // W3.2 (2026-08-04): Internal Summary card removed from R&S sidebar.
  // The internalSummary computation was its only consumer; removed to avoid
  // dead code. If a future surface needs margin metrics, re-derive from
  // the same resolveQuoteDisplay + computeLineItem path.

  // DEF-115 (2026-08-04): R&S reconciliation guard extension. Q-15
  // (2026-08-18) extracted into a pure helper so the option-filter fix
  // can be unit-tested. See computeReconciliationWarning below for details.
  const reconciliationWarning = computeReconciliationWarning(quote);

  // IDI-308: after a send, show the link instead of navigating away.
  if (sendResult) {
    return (
      <SigningLinkResult
        url={sendResult.url}
        documentType="quote"
        deliveryStatus={sendResult.deliveryStatus}
        deliveryDetail={sendResult.deliveryDetail}
        sentTo={sendResult.sentTo}
        recipient={sendResult.recipient}
        expiresAt={sendResult.expiresAt}
        doneLabel="Done"
        onDone={() => navigate('/quotes')}
      />
    );
  }

  return (
    <>
      {/* IDI-254/IDI-280 review (2026-09-13): surface useQuoteAutoSave's
          error/lockedError — mirrors QuoteBuilderPage.js's IDI-313 fix for
          the identical gap. Rendered above the shell so it's visible
          regardless of scroll position, same placement the builder uses. */}
      {rsAutoSaveError && (
        <div className="px-6 pt-4">
          <Alert variant="error">{rsAutoSaveError}</Alert>
        </div>
      )}
      {rsLockedError && (
        <div className="px-6 pt-4">
          <Alert variant="warning">
            <div className="font-medium">This quote is locked</div>
            <div className="text-sm mt-1">
              {rsLockedError.reason || 'This quote has been accepted; its price is frozen.'}
              {' '}Notes and terms can still be edited — to modify pricing, create a Change Order.
            </div>
          </Alert>
        </div>
      )}
      <ReviewAndSendShell
        title={`Quote ${quote?.quote_number || quote?.quoteNumber || ''}`}
        subtitle={[customer?.name || quote?.customer_name, quote?.title].filter(Boolean).join(' · ') || undefined}
        backTo={getQuoteRoute(quote)}
        backLabel="Back to quote"
        status={resolveDisplayStatus(quote?.status, quote?.line_items?.length || quote?.lineItems?.length || 0)}
        loading={loading}
        deliveryNote={
          // requireESign is hardcoded true (W3.6 operator ruling: e-sig is not
          // optional), so this is unconditional — no dead branch for a state that
          // cannot occur. If the checkbox ever returns, branch here.
          'The customer will be emailed a link to review and sign this quote. It stays pending signature until they respond.'
        }
        recipients={recipients}
        onToggleRecipient={(id) => setRecipients(prev => prev.map(r => r.id === id || r.email === id ? {...r, checked: !r.checked} : r))}
        onAddRecipient={(email) => setRecipients(prev => [...prev, {id: `added-${Date.now()}`, email, name: '', checked: true}])}
        displayToggles={DISPLAY_TOGGLES}
        displayOptions={displayOptions}
        onToggleDisplay={(key) => setDisplayOptions(prev => ({...prev, [key]: !prev[key]}))}
        notes={railNotes}
        onNotesChange={setRailNotes}
        onCancel={handleCancel}
        onDownload={handleDownloadPDF}
        // IDI-132: locked when sent or signed. A sent quote can still be
        // resent (same token, IDI-35); a signed or converted one can't be
        // sent again (IDI-549's 409) — Reopen and Edit first.
        locked={!!rsLockState}
        lockedLabel={quoteLockLabel(rsLockState)}
        lockedMessage={
          rsLockState === 'sent'
            ? '— this is the document the customer was sent. To change it, use Reopen and Edit on the quote.'
            : rsLockState === 'signed'
              ? '— this is the document the customer accepted. To change it, use Reopen and Edit on the quote.'
              : rsLockState === 'converted'
                ? '— this quote has been converted to a job.'
                : undefined
        }
        onSend={rsLockState === 'signed' || rsLockState === 'converted' ? undefined : () => setShowConfirm(true)}
        sendBlockedReason={
          rsLockState === 'signed'
            ? 'Signed — use Reopen and Edit to send a new version.'
            : rsLockState === 'converted' ? 'Converted to a job.' : undefined
        }
        onConvertToJob={handleConvertToJob}
        canConvertToJob={canConvertToJob && rsLockState !== 'converted'}
        sending={sending}
        sendDisabled={recipients.filter(r => r.checked).length === 0}
        sendLabel={rsLockState === 'sent' ? 'Resend quote' : 'Send quote'}
        extraRailCards={
          <>
            {/* T&C Template */}
            <Card className="p-4 space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                Terms & Conditions
              </h4>
              <SelectMenu
                value={tcTemplateId}
                onChange={setTcTemplateId}
                width="full"
                placeholder="Select T&C template…"
                options={
                  tcTemplates.length > 0
                    ? tcTemplates.map((t) => ({
                        value: (t.id || t.name || '').toString(),
                        label: t.name || t.label || t.title || 'Untitled template',
                      }))
                    : [
                        { value: 'standard', label: 'Standard Terms' },
                        ...getSettingsOptions('customer_types').map((ct) => ({
                          value: ct.value,
                          label: `${ct.label} Terms`,
                        })),
                      ]
                }
              />
            </Card>

            {/* §1 (2026-08-08, RS-1/RS-3): ATTACHMENTS card.
                Peer to T&C and Notes. Lists every file the quote holds with
                a per-file include checkbox. Users see WHAT is being sent, not
                just a blanket "include attachments" toggle. */}
            <Card className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                  Attachments
                </h4>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setAttachOpen(true)}
                >
                  <PlusIcon className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                  Attach file
                </Button>
              </div>
              {attachedFiles.length === 0 ? (
                <div className="text-xs text-center py-3" style={{ color: 'var(--color-text-muted)' }}>
                  No attachments. Click Attach file to add one.
                </div>
              ) : (
                <ul className="space-y-1">
                  {attachedFiles.map((file, idx) => {
                    const key = file.id || file.attachment_id || file.filename || `att-${idx}`;
                    const name = file.filename || file.name || file.title || 'Untitled';
                    const bytes = Number(file.size || file.size_bytes || 0);
                    const sizeLabel = bytes >= 1024 * 1024
                      ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
                      : bytes > 0
                        ? `${Math.round(bytes / 1024)} KB`
                        : '';
                    return (
                      <li
                        key={key}
                        className="flex items-center gap-2 px-2 py-1.5 rounded text-sm"
                        style={{ backgroundColor: 'var(--neutral-50)' }}
                      >
                        <Checkbox
                          checked={file.included !== false}
                          onChange={(checked) => {
                            setAttachedFiles((prev) => prev.map((f, i) => (
                              i === idx ? { ...f, included: checked } : f
                            )));
                          }}
                          ariaLabel={`Include ${name} in this quote`}
                        />
                        <PaperClipIcon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} aria-hidden="true" />
                        <span className="flex-1 min-w-0" style={{ color: 'var(--color-text-default)' }}>
                          <span className="block truncate" title={name}>{name}</span>
                          {/* IDI-487 (D5): only PDFs and PNG / JPEG images join the downloaded PDF. */}
                          {!canIncludeInPdf(file) && (
                            <span className="block text-xs" style={{ color: 'var(--color-text-muted)' }}>
                              Can't be included in the downloaded PDF
                            </span>
                          )}
                        </span>
                        {sizeLabel && (
                          <span className="text-xs tabular-nums flex-shrink-0" style={{ color: 'var(--color-text-muted)' }}>
                            {sizeLabel}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));
                          }}
                          aria-label={`Remove ${name}`}
                          className="p-1 rounded hover:bg-gray-200"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          <TrashIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </>
        }
      >
        {/* DEF-115: reconciliation warning on R&S */}
        {reconciliationWarning && (
          <div
            className="mb-4 p-3 rounded-md border text-sm"
            style={{ backgroundColor: 'var(--color-warning-bg)', borderColor: 'var(--color-warning-fill)', color: 'var(--color-warning)' }}
            role="alert"
          >
            {reconciliationWarning}
          </div>
        )}
        {/* Live Preview: the QuoteDocument renderer */}
        <QuoteDocument
          viewQuote={viewQuote}
          options={displayOptions}
          branding={branding}
          profile={companyProfile}
        />
      </ReviewAndSendShell>

      {/* Send confirmation / composer modal — stays in the consumer */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="send-composer-title"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !sending && setShowConfirm(false)}
          />
          <div
            className="relative p-6 max-w-lg w-full mx-4 space-y-4 overflow-y-auto max-h-[85vh]"
            style={{
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-surface)',
            }}
            data-portica-role="send-composer"
          >
            <div>
              <h3
                id="send-composer-title"
                className="text-lg font-semibold"
                style={{
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-display)',
                }}
              >
                Send quote
              </h3>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                Sending locks the quote. E-signature acknowledgement is not the same as approval.
              </p>
            </div>

            {/* Recipients (read-only chips; edit in the rail) */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                To
              </label>
              <div className="flex flex-wrap gap-1.5">
                {recipients.filter((r) => r.checked).length === 0 ? (
                  <span className="text-xs italic" style={{ color: 'var(--color-text-secondary)' }}>
                    No recipients selected — pick at least one in the rail before sending.
                  </span>
                ) : (
                  recipients
                    .filter((r) => r.checked)
                    .map((r) => (
                      <span
                        key={r.id || r.email}
                        className="inline-flex items-center px-2 py-0.5 text-xs rounded"
                        style={{
                          backgroundColor: 'var(--color-surface-alt)',
                          border: '1px solid var(--color-border)',
                          color: 'var(--color-text-primary)',
                        }}
                      >
                        {r.name ? `${r.name} <${r.email}>` : r.email}
                      </span>
                    ))
                )}
              </div>
            </div>

            {/* IDI-134: CC — pre-filled with the signed-in user and the
                assigned salesperson(s); each chip removable. */}
            <SendComposerCc
              value={ccEmails}
              onChange={(next) => setCcEmails(next)}
              salespersons={quote?.salespersons}
              salespersonIds={quote?.salesperson_ids}
              assigneeEmail={quote?.assigneeEmail}
              disabled={sending}
            />

            <Input
              label="Subject"
              value={composerSubject}
              onChange={(e) => setComposerSubject(e.target.value)}
              placeholder="Quote 1043"
              aria-label="Email subject"
            />

            <Textarea
              label="Message"
              rows={5}
              value={composerMessage}
              onChange={(e) => setComposerMessage(e.target.value)}
              placeholder="Hi—here's your quote. Reply to this email with any questions."
              aria-label="Email message body"
            />

            {/* Attachments */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Attachments
              </label>
              <div className="space-y-1.5">
                <Checkbox
                  checked={includeQuotePdf}
                  onChange={setIncludeQuotePdf}
                  label="Quote PDF (recommended)"
                />
              </div>
            </div>

            {/* W3.6: e-sig checkbox removed — always requested */}

            {/* PT-49: Honest notice — email delivery not enabled yet */}
            <div
              className="rounded-md p-3 text-xs"
              style={{
                backgroundColor: 'var(--color-surface-sunken)',
                border: '1px solid var(--color-border)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {/* PT-53A W2.1 (DEF-108): banner copy now matches what actually
                  happens. Previously said "marked as Sent" (backend actually
                  marks it Active) and "recipients recorded" (recipients were
                  discarded by the API call). Both now true post-fix.

                  IDI-37: "Email delivery isn't enabled yet" was no longer true —
                  IDI-14 wired SES and a real signature email IS delivered. Leaving
                  that copy would tell a user nothing was sent when it was.
                  requireESign is hardcoded true (W3.6), so this is unconditional. */}
              <strong>Note:</strong> The customer will be emailed a link to review
              and sign this quote. Recipients, subject and message are recorded on
              the quote as an audit trail.
            </div>

            {/* Footer */}
            <div
              className="flex gap-2 justify-end pt-2"
              style={{ borderTop: '1px solid var(--color-border)' }}
            >
              <Button variant="secondary" onClick={() => setShowConfirm(false)} disabled={sending}>
                Cancel
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || recipients.filter((r) => r.checked).length === 0}
              >
                {sending ? 'Sending…' : 'Send quote'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* §1 (2026-08-08): quote-scope attachment upload. On success, append
          the new attachment to the local list with included:true. */}
      <AttachModal
        /* IDI-55: "Choose from library" is on only here and on quote Review & Send + WO Attachments (Adrian 10-21). */
        allowLibrary
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        scope="quote"
        ownerId={id}
        ownerLabel={quote?.title || `Quote ${id?.slice(0, 8)}`}
        companyId={quote?.companyId}
        onAttached={(ref) => {
          setAttachedFiles((prev) => [...prev, { ...ref, included: true }]);
          setAttachOpen(false);
        }}
      />
    </>
  );
}
